import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/ui/Header';
import BarcodeScanner from './components/BarcodeScanner';
import MemberVerification from './components/MemberVerification';
import AttendanceList from './components/AttendanceList';
import ManualCheckInModal from './components/ManualCheckInModal';

const BarcodeAttendanceSystem = () => {
  const { user, currentTenant, isGymOwner, isStaff, isTrainer } = useAuth();
  const [activeTab, setActiveTab] = useState('scanner');
  const [scannedMember, setScannedMember] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState({
    currentOccupancy: 0,
    todayCheckIns: 0,
    averageDuration: 0
  });
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualBarcodeInput, setManualBarcodeInput] = useState('');
  const [error, setError] = useState('');

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (currentTenant?.id && isStaffMember) {
      fetchAttendanceStats();
      fetchRecentAttendance();
    }
  }, [currentTenant?.id, isStaffMember]);

  const fetchAttendanceStats = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      // Get today's check-ins
      const { data: todayLogs, error: todayError } = await supabase
        ?.from('attendance_logs')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('check_in_time', today?.toISOString());

      if (todayError) throw todayError;

      // Calculate current occupancy (checked in but not checked out)
      const currentOccupancy = todayLogs?.filter(log => !log?.check_out_time)?.length || 0;

      // Calculate average duration
      const completedSessions = todayLogs?.filter(log => log?.duration_minutes) || [];
      const avgDuration = completedSessions?.length > 0
        ? Math.round(completedSessions?.reduce((sum, log) => sum + (log?.duration_minutes || 0), 0) / completedSessions?.length)
        : 0;

      setAttendanceStats({
        currentOccupancy,
        todayCheckIns: todayLogs?.length || 0,
        averageDuration: avgDuration
      });
    } catch (error) {
      console.error('Error fetching attendance stats:', error);
    }
  };

  const fetchRecentAttendance = async () => {
    try {
      const { data, error } = await supabase
        ?.from('attendance_logs')
        ?.select(`
          *,
          user_profiles!attendance_logs_user_id_fkey(full_name, email, avatar_url)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('check_in_time', { ascending: false })
        ?.limit(20);

      if (error) throw error;
      setRecentAttendance(data || []);
    } catch (error) {
      console.error('Error fetching recent attendance:', error);
    }
  };

  const handleBarcodeScanned = async (barcodeData) => {
    setLoading(true);
    setError('');
    
    try {
      // Find member by barcode
      const { data: barcodeRecord, error: barcodeError } = await supabase
        ?.from('member_barcodes')
        ?.select(`
          *,
          user_profiles!member_barcodes_user_id_fkey(id, full_name, email, avatar_url),
          tenant_members!inner(role, membership_plan, is_active)
        `)
        ?.eq('barcode_data', barcodeData)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.single();

      if (barcodeError || !barcodeRecord) {
        setError('Invalid or inactive barcode. Please contact staff.');
        return;
      }

      // Check if member is active
      if (!barcodeRecord?.tenant_members?.[0]?.is_active) {
        setError('Membership is inactive. Please contact reception.');
        return;
      }

      // Check if already checked in
      const { data: existingCheckIn, error: checkInError } = await supabase
        ?.from('attendance_logs')
        ?.select('*')
        ?.eq('user_id', barcodeRecord?.user_profiles?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.is('check_out_time', null)
        ?.order('check_in_time', { ascending: false })
        ?.limit(1)
        ?.single();

      if (existingCheckIn) {
        // Check out
        await handleCheckOut(existingCheckIn?.id);
      } else {
        // Check in
        await handleCheckIn(barcodeRecord);
      }

      setScannedMember(barcodeRecord);
      fetchAttendanceStats();
      fetchRecentAttendance();
    } catch (error) {
      console.error('Error processing barcode:', error);
      setError('Error processing barcode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (barcodeRecord) => {
    const { error } = await supabase
      ?.from('attendance_logs')
      ?.insert({
        tenant_id: currentTenant?.id,
        user_id: barcodeRecord?.user_profiles?.id,
        barcode_id: barcodeRecord?.id,
        check_in_time: new Date()?.toISOString(),
        entry_type: 'barcode_scan'
      });

    if (error) throw error;
  };

  const handleCheckOut = async (attendanceLogId) => {
    const { error } = await supabase
      ?.from('attendance_logs')
      ?.update({
        check_out_time: new Date()?.toISOString()
      })
      ?.eq('id', attendanceLogId);

    if (error) throw error;
  };

  const handleManualBarcodeSubmit = () => {
    if (manualBarcodeInput?.trim()) {
      handleBarcodeScanned(manualBarcodeInput?.trim());
      setManualBarcodeInput('');
    }
  };

  if (!isStaffMember) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-card rounded-lg p-8 text-center">
            <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This feature is only available to gym staff, trainers, and owners.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Barcode Attendance System
          </h1>
          <p className="text-muted-foreground">
            Scan member barcodes for quick check-in and attendance tracking
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Current Occupancy</span>
              <Icon name="Users" size={20} className="text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {attendanceStats?.currentOccupancy}
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Today's Check-ins</span>
              <Icon name="LogIn" size={20} className="text-success" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {attendanceStats?.todayCheckIns}
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Avg Duration</span>
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {attendanceStats?.averageDuration} min
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('scanner')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'scanner' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Scan" size={18} className="inline mr-2" />
            Scanner
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'attendance' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="List" size={18} className="inline mr-2" />
            Attendance Log
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'scanner' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Scanner Section */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-xl font-bold mb-4">Barcode Scanner</h2>
              
              <BarcodeScanner
                onScan={handleBarcodeScanned}
                loading={loading}
              />

              {/* Manual Input */}
              <div className="mt-6">
                <p className="text-sm text-muted-foreground mb-3">
                  Or enter barcode manually:
                </p>
                <div className="flex gap-2">
                  <Input
                    value={manualBarcodeInput}
                    onChange={(e) => setManualBarcodeInput(e?.target?.value)}
                    placeholder="Enter barcode number"
                    onKeyPress={(e) => e?.key === 'Enter' && handleManualBarcodeSubmit()}
                    disabled={loading}
                  />
                  <Button
                    onClick={handleManualBarcodeSubmit}
                    disabled={loading || !manualBarcodeInput?.trim()}
                    iconName="Check"
                  >
                    Submit
                  </Button>
                </div>
              </div>

              {/* Manual Check-in Button */}
              <Button
                onClick={() => setShowManualModal(true)}
                variant="outline"
                className="w-full mt-4"
                iconName="UserPlus"
              >
                Manual Check-in
              </Button>

              {error && (
                <div className="mt-4 p-3 bg-destructive/10 border border-destructive rounded-lg">
                  <p className="text-sm text-destructive flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} />
                    {error}
                  </p>
                </div>
              )}
            </div>

            {/* Member Verification */}
            <MemberVerification
              member={scannedMember}
              onClear={() => setScannedMember(null)}
            />
          </div>
        )}

        {activeTab === 'attendance' && (
          <AttendanceList
            attendance={recentAttendance}
            onRefresh={fetchRecentAttendance}
            onCheckOut={handleCheckOut}
          />
        )}
      </div>

      {/* Manual Check-in Modal */}
      {showManualModal && (
        <ManualCheckInModal
          onClose={() => setShowManualModal(false)}
          onSuccess={() => {
            setShowManualModal(false);
            fetchAttendanceStats();
            fetchRecentAttendance();
          }}
        />
      )}
    </div>
  );
};

export default BarcodeAttendanceSystem;